import { fetchAPI, fetchAPIWithAuth } from '../Api';
import { User } from '../store/userSlice';
import {
  HealthResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  LogoutResponse,
  CheckinRequest,
  CheckinResponse,
  UserProfile,
  User as UserApi,
  CreateUserRequest,
  CreateUserResponse,
  UpdatePasswordResponse,
  EmployeeApi,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  ValidateEmployeeRequest,
  WorkRecordRequest,
  WorkRecordResponse,
  SyncDataResponse,
  CompanyApi,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanySettingsRequest,
  CompanySettingsResponse,
  JobSiteApi,
  JobSiteWithStats,
  CreateJobSiteRequest,
  UpdateJobSiteRequest,
  GeocodeRequest,
  GeocodeResponse,
  CostCodeApi,
  CreateCostCodeRequest,
  UpdateCostCodeRequest,
  JobSiteBudgetResponse,
  JobSiteBudgetResponseWithCostCodes,
  JobSiteBudgetItemResponse,
  JobSiteBudgetItemResponseWithCostCode,
  CreateBudgetItemRequest,
  UpdateBudgetItemRequest,
  BulkCreateBudgetRequest,
  DailyReportResponse,
  WeeklyReportResponse,
  SageProjectsResponse,
  SageEmployeesResponse,
  SageBudgetResponse,
  SageBudgetSummaryResponse,
  Position
} from '../store/interfaces';

// Health endpoints
export const healthService = {
  getHealth: async (): Promise<HealthResponse> => {
    const response = await fetchAPI(undefined, 'GET', 'health/');
    if (!response.ok) {
      throw new Error('Health check failed');
    }
    return response.json();
  }
};

// Authentication endpoints
export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await fetchAPI(undefined, 'POST', 'users/login', credentials);
    if (!response.ok) {
      throw new Error('Login failed');
    }
    return response.json();
  },

  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await fetchAPI(undefined, 'POST', 'users/refresh', { refreshToken });
    if (!response.ok) {
      throw new Error('Token refresh failed');
    }
    return response.json();
  },

  logout: async (refreshToken: string): Promise<LogoutResponse> => {
    const response = await fetchAPI(undefined, 'POST', 'users/logout', { refreshToken });
    if (!response.ok) {
      throw new Error('Logout failed');
    }
    return response.json();
  },

  checkin: async (checkinData: CheckinRequest): Promise<CheckinResponse> => {
    const response = await fetchAPI(undefined, 'POST', 'users/checkin', checkinData);
    if (!response.ok) {
      throw new Error('Checkin failed');
    }
    return response.json();
  },

  getProfile: async (user: User): Promise<UserProfile> => {
    const response = await fetchAPIWithAuth(user, 'GET', 'users/profile');
    if (!response.ok) {
      throw new Error('Failed to get user profile');
    }
    return response.json();
  }
};

// User endpoints
export const userService = {
  getUsers: async (user: User): Promise<UserApi[]> => {
    const response = await fetchAPIWithAuth(user, 'GET', 'users/');
    if (!response.ok) {
      throw new Error('Failed to get users');
    }
    return response.json();
  },

  getEmployees: async (user: User): Promise<EmployeeApi[]> => {
    const response = await fetchAPI(user, 'GET', 'users/employees');
    if (!response.ok) {
      throw new Error('Failed to get employees');
    }
    return response.json();
  },

  getSyncData: async (user: User): Promise<SyncDataResponse> => {
    const response = await fetchAPI(user, 'GET', 'users/sync-data');
    if (!response.ok) {
      throw new Error('Failed to get sync data');
    }
    return response.json();
  },

  resetSequences: async (user: User): Promise<{ message: string }> => {
    const response = await fetchAPI(user, 'POST', 'users/reset-sequences');
    if (!response.ok) {
      throw new Error('Failed to reset sequences');
    }
    return response.json();
  },

  getUser: async (user: User, id: number): Promise<UserApi> => {
    const response = await fetchAPI(user, 'GET', `users/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get user');
    }
    return response.json();
  },

  updatePassword: async (user: User, id: number, password: string): Promise<UpdatePasswordResponse> => {
    const response = await fetchAPI(user, 'PATCH', `users/${id}/password`, { password });
    if (!response.ok) {
      throw new Error('Failed to update password');
    }
    return response.json();
  },

  createUser: async (user: User, userData: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await fetchAPI(user, 'POST', 'users/', userData);
    if (!response.ok) {
      throw new Error('Failed to create user');
    }
    return response.json();
  }
};

// Company endpoints
export const companyService = {
  createCompany: async (user: User, companyData: CreateCompanyRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(user, 'POST', 'companies/', companyData);
    if (!response.ok) {
      throw new Error('Failed to create company');
    }
    return response.json();
  },

  getCompanies: async (user: User): Promise<CompanyApi[]> => {
    const response = await fetchAPIWithAuth(user, 'GET', 'companies/');
    if (!response.ok) {
      throw new Error('Failed to get companies');
    }
    return response.json();
  },

  getCompany: async (user: User, id: number): Promise<CompanyApi> => {
    const response = await fetchAPI(user, 'GET', `companies/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get company');
    }
    return response.json();
  },

  updateCompany: async (user: User, id: number, companyData: UpdateCompanyRequest): Promise<CompanyApi> => {
    const response = await fetchAPI(user, 'PATCH', `companies/${id}`, companyData);
    if (!response.ok) {
      throw new Error('Failed to update company');
    }
    return response.json();
  },

  deleteCompany: async (user: User, id: number): Promise<{ message: string }> => {
    const response = await fetchAPI(user, 'DELETE', `companies/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete company');
    }
    return response.json();
  },

  createCompanySettings: async (id: number, settings: CompanySettingsRequest): Promise<CompanySettingsResponse> => {
    const response = await fetchAPI(undefined, 'POST', `companies/settings/${id}`, settings);
    if (!response.ok) {
      throw new Error('Failed to create company settings');
    }
    return response.json();
  },

  getCompanySettings: async (user: User, companyId: number): Promise<CompanySettingsResponse> => {
    const response = await fetchAPI(user, 'GET', `companies/settings/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to get company settings');
    }
    return response.json();
  },

  updateCompanySettings: async (user: User, companyId: number, settings: CompanySettingsRequest): Promise<CompanySettingsResponse> => {
    const response = await fetchAPI(user, 'POST', `companies/settings/${companyId}`, settings);
    if (!response.ok) {
      throw new Error('Failed to update company settings');
    }
    return response.json();
  },

  getCompanyJobSitesWithStats: async (user: User, companyId: number): Promise<JobSiteWithStats[]> => {
    const response = await fetchAPIWithAuth(user, 'GET', `companies/${companyId}/jobsites`);
    if (!response.ok) {
      throw new Error('Failed to get company job sites with stats');
    }
    const data = await response.json();
    return data.jobSites || [];
  }
};

// Job Site endpoints
export const jobSiteService = {
  createJobSite: async (user: User, jobSiteData: CreateJobSiteRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(user, 'POST', 'jobSites/', jobSiteData);
    if (!response.ok) {
      throw new Error('Failed to create job site');
    }
    return response.json();
  },

  getJobSites: async (user: User): Promise<JobSiteApi[]> => {
    const response = await fetchAPI(user, 'GET', 'jobSites/');
    if (!response.ok) {
      throw new Error('Failed to get job sites');
    }
    return response.json();
  },

  getJobSitesByCompany: async (user: User, companyId: number): Promise<JobSiteApi[]> => {
    const response = await fetchAPI(user, 'GET', `jobSites/company/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to get job sites by company');
    }
    return response.json();
  },

  getJobSite: async (user: User | undefined, id: number): Promise<JobSiteApi> => {
    const response = await fetchAPI(user, 'GET', `jobSites/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get job site');
    }
    return response.json();
  },

  getJobSiteWithStats: async (user: User, companyId: number, jobSiteId: number): Promise<JobSiteWithStats> => {
    const response = await fetchAPIWithAuth(user, 'GET', `companies/${companyId}/jobsites/${jobSiteId}`);
    if (!response.ok) {
      throw new Error('Failed to get job site with stats');
    }
    return response.json();
  },

  updateJobSite: async (user: User, id: number, jobSiteData: UpdateJobSiteRequest): Promise<{ id: number }> => {
    const response = await fetchAPIWithAuth(user, 'PATCH', `jobSites/${id}`, jobSiteData);
    if (!response.ok) {
      throw new Error('Failed to update job site');
    }
    return response.json();
  },

  deleteJobSite: async (user: User, id: number): Promise<{ message: string }> => {
    const response = await fetchAPI(user, 'DELETE', `jobSites/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete job site');
    }
    return response.json();
  },

  getJobSitesBySupervisor: async (user: User, supervisorId: number): Promise<JobSiteApi[]> => {
    const response = await fetchAPI(user, 'GET', `jobSites/supervisor/${supervisorId}`);
    if (!response.ok) {
      throw new Error('Failed to get job sites by supervisor');
    }
    return response.json();
  },

  geocodeJobSites: async (user: User, geocodeData?: GeocodeRequest): Promise<GeocodeResponse> => {
    const response = await fetchAPI(user, 'POST', 'jobSites/geocode', geocodeData);
    if (!response.ok) {
      throw new Error('Failed to geocode job sites');
    }
    return response.json();
  },

  getJobSitesWithStats: async (user: User): Promise<JobSiteWithStats[]> => {
    const response = await fetchAPI(user, 'GET', 'jobSites/stats');
    if (!response.ok) {
      throw new Error('Failed to get job sites with stats');
    }
    return response.json();
  }
};

// Employee endpoints
export const employeeService = {
  createEmployee: async (employeeData: CreateEmployeeRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(undefined, 'POST', 'employees/', employeeData);
    if (!response.ok) {
      throw new Error('Failed to create employee');
    }
    return response.json();
  },

  getEmployeesByCompany: async (companyId: number): Promise<EmployeeApi[]> => {
    const response = await fetchAPI(undefined, 'GET', `employees/company/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to get employees by company');
    }
    return response.json();
  },

  getEmployee: async (id: number): Promise<EmployeeApi> => {
    const response = await fetchAPI(undefined, 'GET', `employees/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get employee');
    }
    return response.json();
  },

  updateEmployee: async (id: number, employeeData: UpdateEmployeeRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(undefined, 'PATCH', `employees/${id}`, employeeData);
    if (!response.ok) {
      throw new Error('Failed to update employee');
    }
    return response.json();
  },

  deleteEmployee: async (id: number): Promise<{ message: string }> => {
    const response = await fetchAPI(undefined, 'DELETE', `employees/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete employee');
    }
    return response.json();
  },

  validateEmployee: async (validateData: ValidateEmployeeRequest): Promise<EmployeeApi> => {
    const response = await fetchAPI(undefined, 'POST', 'employees/validate', validateData);
    if (!response.ok) {
      throw new Error('Failed to validate employee');
    }
    return response.json();
  },

  addWorkRecord: async (companyId: number, employeeId: number, workData: WorkRecordRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(undefined, 'POST', `employees/company/${companyId}/employee/${employeeId}/work`, workData);
    if (!response.ok) {
      throw new Error('Failed to add work record');
    }
    return response.json();
  },

  updateWorkRecord: async (companyId: number, employeeId: number, workId: number, workData: Partial<WorkRecordRequest>): Promise<WorkRecordResponse> => {
    const response = await fetchAPI(undefined, 'PATCH', `employees/company/${companyId}/employee/${employeeId}/work/${workId}`, workData);
    if (!response.ok) {
      throw new Error('Failed to update work record');
    }
    return response.json();
  }
};

// Cost Code endpoints
export const costCodeService = {
  createCostCode: async (costCodeData: CreateCostCodeRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(undefined, 'POST', 'costCodes/', costCodeData);
    if (!response.ok) {
      throw new Error('Failed to create cost code');
    }
    return response.json();
  },

  getCostCodesByCompany: async (companyId: number): Promise<CostCodeApi[]> => {
    const response = await fetchAPI(undefined, 'GET', `costCodes/company/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to get cost codes by company');
    }
    return response.json();
  },

  getCostCode: async (id: number): Promise<CostCodeApi> => {
    const response = await fetchAPI(undefined, 'GET', `costCodes/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get cost code');
    }
    return response.json();
  },

  updateCostCode: async (id: number, costCodeData: UpdateCostCodeRequest): Promise<{ id: number }> => {
    const response = await fetchAPI(undefined, 'PATCH', `costCodes/${id}`, costCodeData);
    if (!response.ok) {
      throw new Error('Failed to update cost code');
    }
    return response.json();
  },

  deleteCostCode: async (id: number): Promise<{ message: string }> => {
    const response = await fetchAPI(undefined, 'DELETE', `costCodes/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete cost code');
    }
    return response.json();
  }
};

// Job Site Budget endpoints
export const jobSiteBudgetService = {

  getBudgetItem: async (id: number): Promise<JobSiteBudgetItemResponse> => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/${id}`);
    if (!response.ok) {
      throw new Error('Failed to get budget item');
    }
    return response.json();
  },

  getBudgetItemWithCostCode: async (id: number): Promise<JobSiteBudgetItemResponseWithCostCode> => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/${id}/with-cost-code`);
    if (!response.ok) {
      throw new Error('Failed to get budget item with cost code');
    }
    return response.json();
  },

  createBudgetItem: async (budgetData: CreateBudgetItemRequest): Promise<JobSiteBudgetItemResponse> => {
    const response = await fetchAPI(undefined, 'POST', 'jobsite-budget/', budgetData);
    if (!response.ok) {
      throw new Error('Failed to create budget item');
    }
    return response.json();
  },

  updateBudgetItem: async (id: number, budgetData: UpdateBudgetItemRequest): Promise<JobSiteBudgetItemResponse> => {
    const response = await fetchAPI(undefined, 'PUT', `jobsite-budget/${id}`, budgetData);
    if (!response.ok) {
      throw new Error('Failed to update budget item');
    }
    return response.json();
  },

  deleteBudgetItem: async (id: number): Promise<{ message: string; data: { deleted: boolean } }> => {
    const response = await fetchAPI(undefined, 'DELETE', `jobsite-budget/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete budget item');
    }
    return response.json();
  },

  getBudgetByCostCode: async (costCodeId: number): Promise<JobSiteBudgetResponse> => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/costcode/${costCodeId}`);
    if (!response.ok) {
      throw new Error('Failed to get budget by cost code');
    }
    return response.json();
  },

  getBudgetByCostCodeWithDetails: async (costCodeId: number): Promise<JobSiteBudgetResponseWithCostCodes> => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/costcode/${costCodeId}/with-cost-codes`);
    if (!response.ok) {
      throw new Error('Failed to get budget by cost code with details');
    }
    return response.json();
  },

  bulkCreateBudgetItems: async (jobSiteId: number, budgetData: BulkCreateBudgetRequest): Promise<JobSiteBudgetResponse> => {
    const response = await fetchAPI(undefined, 'POST', `jobsite-budget/jobsite/${jobSiteId}/bulk`, budgetData);
    if (!response.ok) {
      throw new Error('Failed to bulk create budget items');
    }
    return response.json();
  },

  // Utility function to enhance budget data with cost code details
  enhanceBudgetWithCostCodes: async (budgetResponse: JobSiteBudgetResponse): Promise<JobSiteBudgetResponseWithCostCodes> => {
    try {
      // Get all unique cost code IDs from the budget items
      const costCodeIds = Array.from(new Set(budgetResponse.data.map(item => item.costCodeId)));

      // Fetch cost code details for all unique IDs
      const costCodePromises = costCodeIds.map(id => costCodeService.getCostCode(id));
      const costCodes = await Promise.all(costCodePromises);

      // Create a map of cost code ID to cost code details
      const costCodeMap = new Map(costCodes.map(cc => [cc.id, cc]));

      // Enhance budget items with cost code details
      const enhancedData = budgetResponse.data.map(item => {
        const costCode = costCodeMap.get(item.costCodeId);
        return {
          ...item,
          costCode: costCode?.code || '',
          costCodeDescription: costCode?.description || '',
        };
      });

      return {
        message: budgetResponse.message,
        data: enhancedData,
      };
    } catch (error) {
      console.error('Error enhancing budget with cost codes:', error);
      throw new Error('Failed to enhance budget with cost code details');
    }
  },

  getBudgetWithCostCodes: async (jobSiteId: number) => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/jobsite/${jobSiteId}/budgets-with-costcode`);
    if (!response.ok) throw new Error('Failed to fetch budget with cost codes');
    return response.json();
  },

  getBudgetByJobSite: async (jobSiteId: number): Promise<JobSiteBudgetResponse> => {
    const response = await fetchAPI(undefined, 'GET', `jobsite-budget/jobsite/${jobSiteId}`);
    if (!response.ok) {
      throw new Error('Failed to get budget by job site');
    }
    return response.json();
  }
};

// Report endpoints
export const reportService = {
  getDailyReport: async (jobSiteId: number, date?: string): Promise<DailyReportResponse> => {
    const url = date ? `reports/daily/jobsite/${jobSiteId}?date=${date}` : `reports/daily/jobsite/${jobSiteId}`;
    const response = await fetchAPI(undefined, 'GET', url);
    if (!response.ok) {
      throw new Error('Failed to get daily report');
    }
    return response.json();
  },

  getWeeklyReport: async (companyId: number, startDate?: string, endDate?: string): Promise<WeeklyReportResponse> => {
    let url = `reports/weekly/company/${companyId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetchAPI(undefined, 'GET', url);
    if (!response.ok) {
      throw new Error('Failed to get weekly report');
    }
    return response.json();
  }
};

// Data endpoints
export const dataService = {
  getData: async (user: User): Promise<Position[]> => {
    const response = await fetchAPIWithAuth(user, 'GET', 'data/');
    if (!response.ok) {
      throw new Error('Failed to get data');
    }
    return response.json();
  },

  getDataByCompany: async (user: User, companyId: number): Promise<Position[]> => {
    const response = await fetchAPIWithAuth(user, 'GET', `data/company/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to get data by company');
    }
    return response.json();
  },

  createData: async (user: User, data: { name: string; type: string; companyId: number }): Promise<Position> => {
    const response = await fetchAPIWithAuth(user, 'POST', 'data/', data);
    if (!response.ok) {
      throw new Error('Failed to create data');
    }
    return response.json();
  },

  updateData: async (user: User, id: number, data: { name?: string; type?: string }): Promise<Position> => {
    console.log('updateData called with:', { user, id, data });
    const response = await fetchAPIWithAuth(user, 'PATCH', `data/${id}`, data);
    console.log('updateData response status:', response.status);
    console.log('updateData response ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.log('updateData error response:', errorText);
      throw new Error(`Failed to update data: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    console.log('updateData success result:', result);
    return result;
  },

  deleteData: async (user: User, id: number): Promise<void> => {
    const response = await fetchAPIWithAuth(user, 'DELETE', `data/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete data');
    }
  }
};

// Accounting/Sage endpoints
export const accountingService = {
  syncProjects: async (companyId: number): Promise<SageProjectsResponse> => {
    const response = await fetchAPI(undefined, 'GET', `accounting/${companyId}/projects`);
    if (!response.ok) {
      throw new Error('Failed to sync projects');
    }
    return response.json();
  },

  syncEmployees: async (companyId: number): Promise<SageEmployeesResponse> => {
    const response = await fetchAPI(undefined, 'GET', `accounting/${companyId}/employees`);
    if (!response.ok) {
      throw new Error('Failed to sync employees');
    }
    return response.json();
  },

  getBudgetByJobSite: async (companyId: number, jobSiteId: number): Promise<SageBudgetResponse> => {
    const response = await fetchAPI(undefined, 'GET', `accounting/${companyId}/budget/${jobSiteId}`);
    if (!response.ok) {
      throw new Error('Failed to get budget by job site');
    }
    return response.json();
  },

  getAllBudgets: async (companyId: number): Promise<SageBudgetSummaryResponse> => {
    const response = await fetchAPI(undefined, 'GET', `accounting/${companyId}/budget`);
    if (!response.ok) {
      throw new Error('Failed to get all budgets');
    }
    return response.json();
  }
}; 